import { useEffect, useState } from 'react';
import type { PublicShareAsset } from '@xphoto/shared';
import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { useScrimDismiss } from '../components/useScrimDismiss';
import { interpolate } from '../gallery/format';
import { ZoomableImage } from '../viewer/ZoomableImage';
import { mediaUrl, thumbnailUrl } from './api';

/**
 * Vue plein écran de la page publique.
 *
 * Réutilise `ZoomableImage` (zoom/pan, pincer mobile, balayage) tel quel : c'est un
 * composant autonome qui ne connaît ni session ni galerie. Ce qui l'entoure, en
 * revanche, est volontairement dépouillé — ni panneau « Détail », ni note, ni
 * commentaires, ni tags, ni nom de fichier, ni date. Le visiteur voit le média et sa
 * position dans l'ensemble, rien d'autre (ADR-0009).
 *
 * ── LE REPLI SUR LA VIGNETTE ──────────────────────────────────────────────────────
 *
 * Si le média ne se charge pas, on affiche la vignette 1024 plutôt qu'un carré vide.
 * Écrit comme une garde GÉNÉRIQUE (`onError`) et non comme un cas particulier de format.
 *
 * Son déclencheur d'origine — le TIFF, alors accepté par le scanner et rendu par aucun
 * navigateur — a DISPARU en v1.29.0 : le TIFF est désormais converti en WebP à l'entrée,
 * et la bibliothèque ne contient plus que des formats affichables. La garde reste
 * néanmoins, parce qu'elle n'a jamais parlé de format : un `onError` est vrai par
 * construction, quelle qu'en soit la cause (fichier tronqué, transfert interrompu,
 * format futur). C'est précisément ce qui la rend encore juste alors que sa raison
 * initiale n'existe plus.
 *
 * Le fond reste SOMBRE dans les deux thèmes, comme la visionneuse interne : c'est la
 * règle du jeton `--color-overlay`, un média se regarde sur du noir.
 */

interface PublicShareViewerProps {
  token: string;
  assets: readonly PublicShareAsset[];
  /** Rang 1-based du média affiché (celui de l'URL). */
  rank: number;
  /** Le partage sert-il les fichiers d'origine (donc téléchargeables) ? */
  downloadable: boolean;
  onClose: () => void;
  onNavigate: (rank: number) => void;
}

export function PublicShareViewer({
  token,
  assets,
  rank,
  downloadable,
  onClose,
  onNavigate,
}: PublicShareViewerProps): React.JSX.Element | null {
  const { t } = useI18n();
  const [mediaFailed, setMediaFailed] = useState(false);

  const index = rank - 1;
  const asset = assets[index];
  const canPrev = index > 0;
  const canNext = index < assets.length - 1;

  // Un média différent mérite une nouvelle chance : sans cette remise à zéro, un seul
  // échec condamnerait tous les suivants à la vignette.
  useEffect(() => setMediaFailed(false), [asset?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && canPrev) onNavigate(rank - 1);
      if (e.key === 'ArrowRight' && canNext) onNavigate(rank + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rank, canPrev, canNext, onClose, onNavigate]);

  /*
   * FONDS CLIQUABLES (v1.30.3, même passe que la visionneuse interne). Le clic « à côté
   * du média » referme la vue plein écran — `ZoomableImage` le fait déjà pour les images.
   * Restaient deux surfaces qui avalaient le clic sans rien faire :
   *  - le VIDE de la barre du haut, entre le compteur et les actions (une bande pleine
   *    largeur qui n'est ni un contrôle ni un fond) ;
   *  - le pourtour d'une VIDÉO, qui occupait `h-full w-full` : ses bandes noires
   *    d'`object-contain` appartenaient à l'élément `<video>` lui-même, donc AUCUN fond
   *    cliquable sur toute la page.
   * Deux instances distinctes : la garde du geste entier tient un drapeau d'armement,
   * deux surfaces ne peuvent pas le partager.
   */
  const topBarScrim = useScrimDismiss(onClose);
  const mediaScrim = useScrimDismiss(onClose);

  if (asset === undefined) return null;

  const src = mediaFailed ? thumbnailUrl(token, asset, 1024) : mediaUrl(token, asset);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={interpolate(t('publicShare.viewerLabel'), { n: rank, total: assets.length })}
      className="fixed inset-0 z-50 flex flex-col bg-overlay/95"
    >
      <div {...topBarScrim} className="flex items-center justify-between gap-3 p-3 text-white">
        <span className="ps-1 text-sm tabular-nums text-white/80">
          {interpolate(t('publicShare.position'), { n: rank, total: assets.length })}
        </span>
        {/* `-m-2 p-2` : marge de sécurité INERTE de 8 px autour des actions — mêmes pixels
            à l'écran, boîte de clic élargie. Viser « Télécharger » et le manquer de peu ne
            doit pas refermer la vue. Le compteur, lui aussi descendant, est inerte. */}
        <div className="-m-2 flex items-center gap-1 p-2">
          {downloadable && (
            <a
              href={mediaUrl(token, asset)}
              download
              title={t('publicShare.download')}
              aria-label={t('publicShare.download')}
              className="rounded-md p-2.5 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            title={t('publicShare.close')}
            aria-label={t('publicShare.close')}
            className="rounded-md p-2.5 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        {asset.kind === 'video' ? (
          // Vidéo CENTRÉE dans un fond cliquable (même montage que la visionneuse interne)
          // au lieu d'un `<video>` étalé sur toute la frame. La garde du geste entier n'est
          // pas un luxe : sans elle, glisser la barre de lecture et relâcher un pixel à
          // côté produit un `click` sur le fond — et refermerait la vue en pleine manœuvre.
          <div {...mediaScrim} className="flex h-full w-full items-center justify-center">
            <video
              key={asset.id}
              src={mediaUrl(token, asset)}
              poster={thumbnailUrl(token, asset, 1024)}
              controls
              playsInline
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <ZoomableImage
            key={asset.id}
            src={src}
            alt=""
            naturalWidth={asset.width}
            naturalHeight={asset.height}
            onError={() => setMediaFailed(true)}
            onBackdropClick={onClose}
            onPrev={() => onNavigate(rank - 1)}
            onNext={() => onNavigate(rank + 1)}
            canPrev={canPrev}
            canNext={canNext}
          />
        )}

        {canPrev && (
          <button
            type="button"
            onClick={() => onNavigate(rank - 1)}
            title={t('publicShare.previous')}
            aria-label={t('publicShare.previous')}
            className="absolute start-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2.5 text-white hover:bg-black/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ChevronLeftIcon className="h-7 w-7" aria-hidden="true" />
          </button>
        )}
        {canNext && (
          <button
            type="button"
            onClick={() => onNavigate(rank + 1)}
            title={t('publicShare.next')}
            aria-label={t('publicShare.next')}
            className="absolute end-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2.5 text-white hover:bg-black/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ChevronRightIcon className="h-7 w-7" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
